/**
 * Phase O — Per-section Mobile FG Put-Away.
 *
 * Mobile-optimised flow for `/sections/dispatch/:section/mobile/fg-put-away`.
 * Pack-level put-away within a bundle: scan bundle → suggested bin →
 * scan each pack barcode → confirm. Backend uses
 * `/api/per-section/{slug}/fg-put-away/...` (Phase D7). Salem's
 * `MobileFGPutAway.jsx` is paired with this file in row 10.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, User, Package, MapPin, CheckCircle, RotateCcw, AlertTriangle, Box } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo',
  art_silk: 'Art Silk',
  kids: 'Kids',
};

const PerSectionMobileFgPutAway = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug];
  const mobileHome = `/sections/dispatch/${slug}/mobile`;
  const apiBase = `/per-section/${slug}/fg-put-away`;

  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);
  const packInputRef = useRef(null);
  const binInputRef = useRef(null);

  const [step, setStep] = useState('staff'); // staff, bundle, pack, success
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [bundleCode, setBundleCode] = useState('');
  const [bundleDetails, setBundleDetails] = useState(null);
  const [suggestedBin, setSuggestedBin] = useState(null);
  const [packBarcode, setPackBarcode] = useState('');
  const [binCode, setBinCode] = useState('');
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionCount, setSessionCount] = useState(0);

  useEffect(() => {
    if (step === 'staff') setTimeout(() => staffInputRef.current?.focus(), 100);
    else if (step === 'bundle') setTimeout(() => bundleInputRef.current?.focus(), 100);
    else if (step === 'pack') setTimeout(() => packInputRef.current?.focus(), 100);
  }, [step]);

  const vibrate = (pattern = [50]) => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(pattern);
  };

  const handleStaffValidate = async () => {
    if (!staffId.trim()) {
      toast.error('Please scan Staff ID');
      vibrate([100, 50, 100]);
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.get(`/validate/staff/${staffId}`);
      if (response.data.valid) {
        setStaffName(response.data.staff_name);
        setStep('bundle');
        vibrate([50]);
        toast.success(`Welcome, ${response.data.staff_name}!`);
      } else {
        toast.error('Invalid Staff ID');
        vibrate([100, 50, 100]);
      }
    } catch (error) {
      toast.error('Staff validation failed');
      vibrate([100, 50, 100]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestedBin = async (skuCode) => {
    if (!skuCode) return;
    try {
      const response = await apiClient.post(`${apiBase}/suggest-bin/${encodeURIComponent(skuCode)}`);
      setSuggestedBin(response.data.suggested_bin || null);
      if (response.data.suggested_bin?.bin_code) {
        setBinCode(response.data.suggested_bin.bin_code);
      }
    } catch (_error) {
      setSuggestedBin(null);
    }
  };

  const handleBundleScan = async () => {
    if (!bundleCode.trim()) {
      toast.error('Please scan bundle barcode');
      vibrate([100, 50, 100]);
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.post(
        `${apiBase}/validate-bundle/${encodeURIComponent(bundleCode)}`
      );
      const bundle = response.data.bundle;
      setBundleDetails(bundle);
      setProgress({
        putaway_count: bundle.putaway_count,
        total_packs: bundle.total_packs,
        remaining_packs: bundle.remaining_packs,
      });
      await fetchSuggestedBin(bundle.sku_code);
      setStep('pack');
      vibrate([50]);
      toast.success(`Bundle ready · ${bundle.remaining_packs}/${bundle.total_packs} packs left`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bundle not found');
      vibrate([100, 50, 100]);
      setBundleCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPack = async () => {
    if (!packBarcode.trim()) {
      toast.error('Please scan pack barcode');
      vibrate([100, 50, 100]);
      packInputRef.current?.focus();
      return;
    }
    if (!binCode.trim()) {
      toast.error('Please enter bin location');
      vibrate([100, 50, 100]);
      binInputRef.current?.focus();
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.post(`${apiBase}/pack`, {
        staff_id: staffId,
        bundle_code: bundleCode,
        pack_barcode: packBarcode,
        bin_code: binCode,
      });
      vibrate([50, 100, 50]);
      toast.success(response.data.message || 'Pack stored!');
      setSessionCount((p) => p + 1);
      setProgress(response.data.progress || progress);
      setPackBarcode('');

      if (response.data.progress?.bundle_complete) {
        setStep('success');
        setTimeout(() => {
          setBundleCode('');
          setBundleDetails(null);
          setSuggestedBin(null);
          setBinCode('');
          setProgress(null);
          setStep('bundle');
        }, 2000);
      } else {
        // Same bundle — focus next pack scan
        setTimeout(() => packInputRef.current?.focus(), 100);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Pack put-away failed');
      vibrate([100, 50, 100]);
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setStaffId('');
    setStaffName('');
    setBundleCode('');
    setBundleDetails(null);
    setSuggestedBin(null);
    setPackBarcode('');
    setBinCode('');
    setProgress(null);
    setSessionCount(0);
    setStep('staff');
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') action();
  };

  if (!sectionName) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center p-6">
        <AlertTriangle className="w-10 h-10 text-amber-400 mb-3" />
        <h2 className="text-lg font-bold">Unknown section</h2>
        <p className="text-sm text-gray-400 mt-1">No catalog entry for slug <code className="font-mono">{slug}</code>.</p>
      </div>
    );
  }

  const stepOrder = ['staff', 'bundle', 'pack', 'success'];
  const currentStepIdx = stepOrder.indexOf(step);
  const packPct = progress && progress.total_packs > 0
    ? Math.round((progress.putaway_count / progress.total_packs) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col" data-testid={`per-section-mobile-fg-put-away-${slug}`}>
      <header className="bg-gradient-to-r from-teal-600 to-teal-800 px-4 py-4 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(mobileHome)}
            className="text-white hover:bg-white/20 -ml-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div className="text-center">
            <h1 className="text-xl font-bold">FG Put-Away</h1>
            <p className="text-xs text-teal-200">{sectionName} Section{staffName ? ` · ${staffName}` : ''}</p>
          </div>
          <div className="w-10" />
        </div>
        <div className="mt-4 flex items-center gap-2">
          {stepOrder.map((s, i) => (
            <div key={s} className={`flex-1 h-2 rounded-full ${i <= currentStepIdx ? 'bg-white' : 'bg-white/30'}`} />
          ))}
        </div>
        {step === 'pack' && progress && (
          <div className="mt-3" data-testid="pack-progress-row">
            <div className="flex justify-between text-sm mb-1">
              <span>{progress.putaway_count} of {progress.total_packs} packs</span>
              <span>{packPct}%</span>
            </div>
            <Progress value={packPct} className="h-2 bg-teal-900" />
          </div>
        )}
      </header>

      {step !== 'staff' && (
        <div className="bg-gray-800 px-4 py-2 flex items-center justify-between text-sm">
          <span className="text-gray-400">Session Packs:</span>
          <span className="font-bold text-teal-400" data-testid="session-counter">{sessionCount}</span>
        </div>
      )}

      <main className="flex-1 p-4 flex flex-col">
        {step === 'staff' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-6 space-y-6">
              <div className="w-20 h-20 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto">
                <User className="w-10 h-10 text-teal-400" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">Scan Staff ID</h2>
                <p className="text-gray-400">Scan your badge to begin</p>
              </div>
              <Input
                ref={staffInputRef}
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleStaffValidate)}
                placeholder="Staff ID"
                className="h-16 text-xl text-center bg-gray-700 border-gray-600 rounded-xl"
                data-testid="mobile-staff-input"
              />
              <Button
                onClick={handleStaffValidate}
                disabled={loading || !staffId}
                className="w-full h-16 text-xl font-bold bg-teal-600 hover:bg-teal-700 rounded-xl"
                data-testid="mobile-staff-validate-btn"
              >
                {loading ? 'Validating...' : 'Continue'}
              </Button>
            </div>
          </div>
        )}

        {step === 'bundle' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-6 space-y-6">
              <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
                <Package className="w-10 h-10 text-blue-400" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">Scan Bundle</h2>
                <p className="text-gray-400">Scan the FG bundle barcode</p>
              </div>
              <Input
                ref={bundleInputRef}
                value={bundleCode}
                onChange={(e) => setBundleCode(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleBundleScan)}
                placeholder="Bundle Barcode"
                className="h-16 text-xl text-center bg-gray-700 border-gray-600 rounded-xl font-mono"
                data-testid="mobile-bundle-input"
              />
              <Button
                onClick={handleBundleScan}
                disabled={loading || !bundleCode}
                className="w-full h-16 text-xl font-bold bg-blue-600 hover:bg-blue-700 rounded-xl"
                data-testid="mobile-bundle-validate-btn"
              >
                {loading ? 'Validating...' : 'Validate Bundle'}
              </Button>
            </div>
          </div>
        )}

        {step === 'pack' && bundleDetails && (
          <div className="flex-1 flex flex-col space-y-4">
            <div className="bg-gray-800 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-400">Bundle</span>
                <span className="font-mono font-bold text-lg" data-testid="bundle-code-display">{bundleCode}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-700 rounded-xl p-3">
                  <p className="text-gray-400 text-xs">SKU</p>
                  <p className="font-medium truncate">{bundleDetails.sku_code || '-'}</p>
                </div>
                <div className="bg-gray-700 rounded-xl p-3">
                  <p className="text-gray-400 text-xs">Total Packs</p>
                  <p className="font-bold text-xl">{bundleDetails.total_packs ?? '-'}</p>
                </div>
              </div>
            </div>

            {suggestedBin && (
              <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-2xl p-4" data-testid="suggested-bin-card">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-green-200">Suggested Location</p>
                    <p className="text-2xl font-bold">{suggestedBin.bin_code}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-gray-800 rounded-2xl p-4 space-y-3">
              <div className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wide">
                <Box className="w-4 h-4" />
                <span>Scan Pack</span>
              </div>
              <Input
                ref={packInputRef}
                value={packBarcode}
                onChange={(e) => setPackBarcode(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleConfirmPack)}
                placeholder="Pack Barcode"
                className="h-14 text-lg text-center bg-gray-700 border-gray-600 rounded-xl font-mono"
                data-testid="mobile-pack-input"
              />
              <Input
                ref={binInputRef}
                value={binCode}
                onChange={(e) => setBinCode(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleConfirmPack)}
                placeholder="Bin Location"
                className="h-14 text-lg text-center bg-gray-700 border-gray-600 rounded-xl font-mono"
                data-testid="mobile-bin-input"
              />
            </div>

            <div className="mt-auto space-y-3">
              <Button
                onClick={handleConfirmPack}
                disabled={loading || !packBarcode || !binCode}
                className="w-full h-16 text-xl font-bold bg-green-600 hover:bg-green-700 rounded-xl"
                data-testid="mobile-confirm-pack-btn"
              >
                {loading ? 'Processing...' : 'Confirm Pack'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setBundleCode('');
                  setBundleDetails(null);
                  setSuggestedBin(null);
                  setBinCode('');
                  setPackBarcode('');
                  setProgress(null);
                  setStep('bundle');
                }}
                className="w-full h-12 border-gray-600 text-gray-300 hover:bg-gray-800 rounded-xl"
                data-testid="mobile-cancel-rescan-btn"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                Switch Bundle
              </Button>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-8 text-center space-y-6" data-testid="mobile-success-card">
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto animate-bounce">
                <CheckCircle className="w-14 h-14 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-green-400">Bundle FG-Done!</h2>
                <p className="text-gray-400 mt-2">All packs put away</p>
              </div>
              <p className="text-sm text-gray-500">Ready for next bundle...</p>
            </div>
          </div>
        )}
      </main>

      {step !== 'staff' && step !== 'success' && (
        <footer className="bg-gray-800 px-4 py-3 safe-area-inset-bottom">
          <Button
            variant="ghost"
            onClick={resetAll}
            className="w-full text-gray-400 hover:text-white"
            data-testid="mobile-end-session-btn"
          >
            End Session
          </Button>
        </footer>
      )}
    </div>
  );
};

export default PerSectionMobileFgPutAway;
